import React from 'react';
import { Inbox, Check } from 'lucide-react';

/**
 * EmptyState — empty list / first-run placeholder.
 *
 * Per Design Principles §3.15.1, every list view has an empty state
 * with a small abstract illustration, one descriptive sentence, a
 * primary CTA, and optional "Learn more" secondary link.
 *
 * For tenant-level emptiness ("you have zero Agentlets yet"), pass a
 * `checklist` array to render the §3.15.4 First-5-Things pattern. Each
 * checklist item: `{ label, action?, done? }`.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.icon] — defaults to Lucide Inbox
 * @param {string}          props.title
 * @param {string}          [props.description]
 * @param {React.ReactNode} [props.primaryAction]
 * @param {React.ReactNode} [props.secondaryAction]
 * @param {Array<{label: string, action?: React.ReactNode, done?: boolean}>} [props.checklist]
 * @param {string}          [props.className]
 */
export function EmptyState({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  checklist,
  className = '',
}) {
  return (
    <div
      role="region"
      aria-label={title}
      className={
        'bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm ' +
        'p-8 flex flex-col items-center text-center ' + className
      }
    >
      <div
        aria-hidden="true"
        className="w-12 h-12 rounded-v2-md bg-v2-action-soft text-v2-action flex items-center justify-center mb-4"
      >
        {icon || <Inbox size={20} />}
      </div>

      <h3 className="text-[16px] font-semibold text-v2-ink m-0 mb-1.5">{title}</h3>

      {description && (
        <p className="text-[13px] text-v2-ink-secondary leading-relaxed max-w-md mb-5">
          {description}
        </p>
      )}

      {checklist && checklist.length > 0 && (
        <ol className="text-left w-full max-w-sm space-y-2 mb-5 list-none p-0">
          {checklist.map((item, i) => (
            <li key={i} className="flex items-center gap-3 text-[13px]">
              <span
                aria-hidden="true"
                className={
                  'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ' +
                  (item.done
                    ? 'bg-v2-success-soft text-v2-success'
                    : 'bg-v2-muted text-v2-ink-muted')
                }
              >
                {item.done ? <Check size={12} aria-hidden="true" /> : i + 1}
              </span>
              <span className={item.done ? 'text-v2-ink-muted line-through' : 'text-v2-ink'}>
                {item.label}
              </span>
              {item.action && <span className="ml-auto">{item.action}</span>}
            </li>
          ))}
        </ol>
      )}

      {(primaryAction || secondaryAction) && (
        <div className="flex items-center gap-3">
          {primaryAction}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}

export default EmptyState;
